import {
  useState,
  useRef,
  useEffect,
  ChangeEvent,
  SyntheticEvent,
  useMemo,
  FormEventHandler,
} from "react";
import { useSnackbar } from "notistack";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { PaperProps } from "@mui/material/Paper";

import { addUser, UserInfo } from "../../utils/api";
import MenuItem from "@mui/material/MenuItem";

interface AddExcludeUserProps {
  value?: string;
  open: boolean;
  onClose: (user?: UserInfo) => void;
}

export default function AddExcludeUser(props: AddExcludeUserProps) {
  const { onClose, value: valueProp, open, ...other } = props;
  const [value, setValue] = useState(valueProp || "");
  const [show, setShow] = useState(false);
  const [type, setType] = useState("User");
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const radioGroupRef = useRef<HTMLDivElement>(null);

  const isError = useMemo(() => {
    if (!value) return false;
    if (value.trim() === "") return false;
    return !/^[a-z\d](?:[a-z\d]|-(?=[a-z\d])){0,38}$/i.test(value);
  }, [value]);

  useEffect(() => {
    if (!open) {
      setValue(valueProp || "");
    }
  }, [valueProp, open]);

  const handleEntering = () => {
    if (radioGroupRef.current != null) {
      radioGroupRef.current.focus();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSubmit: FormEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setLoading(true);
    addUser({
      login: value,
      type,
      show,
    })
      .then((data) => {
        setLoading(false);
        enqueueSnackbar("Successfully!", {
          variant: "success",
        });
        onClose(data);
      })
      .catch((e) => {
        setLoading(false);
        enqueueSnackbar(e.message || "faile", {
          variant: "error",
        });
      });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value);
  };

  const handleChangeType = (event: ChangeEvent<HTMLInputElement>) => {
    setType(event.target.value);
  };

  const handleChangeShow = (
    e: SyntheticEvent<Element, Event>,
    checked: boolean
  ) => {
    setShow(checked);
  };

  if (!open) return null;

  return (
    <Dialog
      maxWidth="xs"
      TransitionProps={{ onEntering: handleEntering }}
      open={open}
      {...other}
      onClose={handleCancel}
      PaperProps={
        { component: "form", onSubmit: handleSubmit } as unknown as Partial<
          PaperProps<"div", {}>
        >
      }
    >
      <DialogTitle>Add exclude user</DialogTitle>
      <DialogContent dividers>
        <FormControl fullWidth>
          <TextField
            label="GitHub login"
            variant="standard"
            error={isError}
            helperText={isError ? "GitHub login error." : " "}
            autoFocus
            value={value}
            inputRef={radioGroupRef}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl fullWidth>
          <TextField
            select
            label="Select account type"
            value={type}
            onChange={handleChangeType}
            helperText=" "
            variant="standard"
          >
            <MenuItem value="User">User</MenuItem>
            <MenuItem value="Organization">Organization</MenuItem>
          </TextField>
        </FormControl>
        <FormControl fullWidth>
          <FormControlLabel
            control={<Switch />}
            checked={show}
            onChange={handleChangeShow}
            labelPlacement="start"
            label="Show profile & repositories"
            sx={{ ml: 0, justifyContent: "space-between" }}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button type="button" disabled={loading} onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading || value.trim() === "" || isError}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
